import { useState } from "react";
import { Download, FileText, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { diasParaVencer, formatBytes, formatRelative } from "@/lib/documentos";
import { documentosService } from "@/services/documentos.service";
import type { Documento } from "@/types";

interface DocumentoListProps {
  documentos: Documento[];
  loading?: boolean;
  emptyMessage?: string;
  onChanged: () => void;
  showLicitacion?: boolean;
}

export function DocumentoList({
  documentos,
  loading,
  emptyMessage = "No hay documentos en esta carpeta.",
  onChanged,
  showLicitacion = false,
}: DocumentoListProps) {
  const [pendingDelete, setPendingDelete] = useState<Documento | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function handleDownload(doc: Documento) {
    setBusyId(doc.id);
    try {
      const { url } = await documentosService.downloadUrl(doc.id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo generar la descarga");
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    setBusyId(pendingDelete.id);
    try {
      await documentosService.remove(pendingDelete.id);
      toast.success("Documento eliminado");
      setPendingDelete(null);
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo eliminar el documento");
    } finally {
      setBusyId(null);
    }
  }

  if (loading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (documentos.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border p-8 text-center">
        <FileText className="mx-auto size-6 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <>
      <ul className="divide-y divide-border rounded-xl border border-border bg-card">
        {documentos.map((doc) => {
          const dias = diasParaVencer(doc.vigenciaHasta);
          return (
            <li key={doc.id} className="flex items-center gap-3 px-3 py-2.5">
              <span className="grid size-9 shrink-0 place-items-center rounded-md bg-muted text-[10px] font-semibold">
                {doc.tipo}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{doc.nombre}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {showLicitacion && doc.licitacion ? `${doc.licitacion.codigo} · ` : ""}
                  {doc.carpeta} · {formatBytes(doc.sizeBytes) !== "—" ? formatBytes(doc.sizeBytes) : doc.tamano} ·{" "}
                  {doc.actualizado ?? formatRelative(doc.updatedAt)}
                </p>
              </div>
              <div className="hidden items-center gap-1.5 md:flex">
                {doc.tags.slice(0, 2).map((tag) => (
                  <span key={tag} className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                    {tag}
                  </span>
                ))}
                {dias !== null ? (
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[10px]",
                      dias < 0
                        ? "bg-destructive/10 text-destructive"
                        : dias <= 30
                          ? "bg-amber-500/10 text-amber-600"
                          : "bg-muted text-muted-foreground",
                    )}
                  >
                    {dias < 0 ? "Vencido" : `Vence en ${dias} d`}
                  </span>
                ) : null}
                {doc.estadoCarga !== "subido" ? (
                  <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-600">
                    {doc.estadoCarga}
                  </span>
                ) : null}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Descargar ${doc.nombre}`}
                  disabled={busyId === doc.id}
                  onClick={() => void handleDownload(doc)}
                >
                  {busyId === doc.id ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Download className="size-4" />
                  )}
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Eliminar ${doc.nombre}`}
                  onClick={() => setPendingDelete(doc)}
                >
                  <Trash2 className="size-4 text-destructive" />
                </Button>
              </div>
            </li>
          );
        })}
      </ul>

      <AlertDialog open={Boolean(pendingDelete)} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar documento</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará «{pendingDelete?.nombre}» del repositorio y del bucket. Esta acción no se
              puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(event) => {
                event.preventDefault();
                void handleDelete();
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
